/**
 * `useAttachArtifacts` mutation hook.
 *
 * Wraps the raw `attachArtifacts` service fn. Cache invalidation
 * targets list queries via `PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(projectId)`
 * so the Library grid refreshes across all filter/sort/page combinations
 * without unnecessarily clearing attachedIds or detail queries.
 *
 * Toast side effects live here (success "Artifacts added" / error
 * "Failed to add artifacts") — mirrors `useSetFolderThumbnail` /
 * `useDownloadFolder` precedent. The picker handles modal-close on success
 * via the mutation's per-call `onSuccess` override.
 */
import type { AttachArtifactsRequestDto } from '@inference.engine/workspace-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PROJECT_ARTIFACT_QUERY_KEYS } from '@/entities/project-artifact';
import { notificationService } from '@/lib/services/notification.service';
import { attachArtifacts } from './project-artifacts.api';

export { attachArtifacts };

interface UseAttachArtifactsOptions {
  projectId: string;
}

export function useAttachArtifacts(options: UseAttachArtifactsOptions) {
  const { t } = useTranslation('projects');
  const queryClient = useQueryClient();
  const { projectId } = options;

  return useMutation({
    mutationFn: (body: AttachArtifactsRequestDto) => attachArtifacts(projectId, body),
    onSuccess: () => {
      // Invalidate only list queries (all filter/sort/page combos) without
      // clearing attachedIds or detail queries unnecessarily.
      void queryClient.invalidateQueries({
        queryKey: PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(projectId),
      });
      notificationService.showSuccess(
        t('manageProjectArtifacts.useAttachArtifacts.artifactsAdded'),
      );
    },
    onError: () => {
      notificationService.showError(
        t('manageProjectArtifacts.useAttachArtifacts.failedToAddArtifacts'),
      );
    },
  });
}
